import { html2canvas } from "@/app/report/common/export/lazyLibs"
import { useEffect, useMemo, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { isNil } from "lodash"

import { GridColDef } from "@mui/x-data-grid"

import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { ACTIVITY_SESSION_TYPE, DEFAULT_EXPORT_OPTIONS } from "@/app/report/common/constants"
import {
  SectionTacticMap,
  Sort,
  TableHeaderColumn,
  TacticTableData,
} from "@/app/report/common/constructor"

import SectionContainer from "@/app/report/components/SectionContainer"
import SectionMenu from "@/app/report/components/SectionMenu"
import TrajectoryMap from "@/app/report/components/map/TrajectoryMap"

import TimeRangeSliderContainer from "@/app/report/components/timeSlider/TimeRangeSliderContainer.tsx"
import { TimeChangeData } from "@/app/report/components/timeSlider/timeTypes.ts"
import {
  useReportPlayerActivityAction18ZoneQuery,
  useReportPlayerActivityActionDetailsQuery,
  useReportPlayerActivityFormationMapQuery,
  useReportPlayerActivityLocation18ZoneQuery,
  useReportPlayerActivityLocationHeatmapQuery,
  useReportPlayerSessionAction18ZoneQuery,
  useReportPlayerSessionActionDetailsQuery,
  useReportPlayerSessionFormationMapQuery,
  useReportPlayerSessionLocation18ZoneQuery,
  useReportPlayerSessionLocationHeatmapQuery,
} from "@/hooks/api/useReportQuery"
import {
  ActionDetails,
  Formation,
  FormationPoint,
  PlayerHeatmapSummary,
  TrajectoryBounds,
  TrajectoryData,
} from "@/types/report"

import { colors } from "@/components/colors"
import { convertUnitSquare } from "@/utils/helper"
import {
  getDecimalPoint,
  getMetricValueStr,
  getTextWidth,
  parseUnit,
} from "@/app/report/common/tools.ts"

import { SORT_ORDER } from "@/constants/setting"
import { ActivityDetail } from "@/types/activity"

import Table from "@/components/table/Table"
import { overflowPagination } from "@/components/table/Pagination"

import { useMetricList } from "@/hooks/api/useMetricQuery"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { MetricMapType } from "@/app/report/common/type"

import "./tacticMapSection.scss"
import TacticMapFilter from "@/app/report/components/section/TacticMapFilter"
import { createAccountStore } from "@/stores/account"
import { getCourtSvg, getCourtField, showFormationMeasurements } from "@/constants/sportConfig"
import ZoneMap, { ZoneData } from "@/app/report/components/map/ZoneMap"
import HeatMap, { PointData } from "@/app/report/components/map/Heatmap.tsx"
import FormationMap from "@/app/report/components/map/FormationMap.tsx"
import { useSearchParams } from "@/hooks/useSearchParams.ts"
import MapRotationSelector from "@/app/report/components/map/MapRotationSelector.tsx"
import { inlineStylesheetsIntoClone } from "@/app/report/common/export/inlineStylesheets"
import { PDF } from "@/app/report/common/export/pdf"
import { exportTableFromView } from "@/app/report/common/export/exportTable"
import { getDateTimeString } from "@/app/report/common/tools"

/**
 * 출력용 표의 컬럼 최소 폭. 헤더 글자 폭만으로 정하면 값이 헤더보다 긴 컬럼이 좁아진다
 * — 등번호 칸에는 세션 평균 행의 라벨(예: "세션 평균")도 들어간다.
 */
const COMPACT_MIN_COLUMN_WIDTH_DEFAULT = 34
const COMPACT_MIN_COLUMN_WIDTH: Record<string, number> = {
  uniformNumber: 90,
}

const DEFAULT_COLUMNS = [
  { key: "number", unit: "number" },
  { key: "uniformNumber", unit: "uniformNumber" },
  { key: "occurrence", unit: "occurrence" },
  { key: "duration", unit: "duration" },
  { key: "distance", unit: "action_distance" },
  { key: "maxSpeed", unit: "maxSpeed" },
  { key: "maxAcceleration", unit: "maxAcceleration" },
  { key: "maxDeceleration", unit: "maxDeceleration" },
  { key: "interval", unit: "interval" },
]

interface SessionRotation {
  idfSession: number
  name: string
  isRoration: boolean
  startMinutes: number
  endMinutes: number
}

export default function TacticMapSection({
  reportSection,
  loading,
  activity,
  idfSessions,
  idfPlayers,
  compactTable = false,
  tableRowRange,
  onRowCount,
  onRowGroups,
  actionQuerySessionIds,
  actionQueryPlayerIds,
  visibleColumns,
  activitySessionType,
  variant = "full",
  hideControls = false,
  mapType,
  actionType,
  color,
  fitColumns = false,
  showRotationSelector,
}: {
  reportSection: SectionTacticMap
  loading: boolean
  activity: ActivityDetail
  idfSessions: number[]
  idfPlayers: number[]
  /**
   * 액티비티/세션 모드. 리포트 화면은 URL(dailyReportType)에서 읽지만 PDF 빌더는 URL 파라미터가 없어
   * Report Filters 의 모드를 직접 넘긴다 (#75). 넘기면 URL 보다 우선.
   */
  activitySessionType?: ACTIVITY_SESSION_TYPE
  /** 그릴 부분 — full(맵+표, 화면 기본) / map(맵만) / table(액션 표만). PDF 빌더의 Map·Action Table 아이템용 */
  variant?: "full" | "map" | "table"
  /** 제목·필터 메뉴·시간 슬라이더·회전 선택기를 그리지 않는다 (PDF 빌더 캔버스 안에 넣을 때) */
  hideControls?: boolean
  /** 맵 종류 강제 (없으면 템플릿 defaultMapType 으로 시작해 필터에서 바꾼다) */
  mapType?: string
  /** 액션 종류 강제 (없으면 템플릿 defaultActionType) */
  actionType?: string
  /** 맵 색 강제 (없으면 템플릿 color) */
  color?: string
  /**
   * 액션 표 컬럼을 컨테이너 폭에 맞춰 나눈다 (PDF 빌더용) — 모든 컬럼이 같은 비율로 남는 폭을 나눠 갖고,
   * 사용자가 컬럼 폭을 개별 조절할 수 없다. 최소 폭은 글자 실측값이라 머리글이 잘리지 않는다.
   */
  fitColumns?: boolean
  /** 컨트롤을 숨겨도 코트 위 180° 회전 선택기(호버)는 보이게 한다 — PDF 빌더 Map (기본: hideControls 의 반대) */
  showRotationSelector?: boolean
  /**
   * 액션 표 컬럼 최소 폭을 글자 폭에 바짝 맞춘다 (PDF 출력용).
   * 화면에서는 정렬 아이콘·넉넉한 패딩이 필요하지만 출력본에는 둘 다 불필요해,
   * 이걸 켜면 같은 글자 크기로 표 폭이 1000px → 630px 수준으로 줄어 맵과 가로로 나란히 놓인다.
   */
  compactTable?: boolean
  /**
   * 액션 표에서 이 구간만 그린다 (PDF 출력용).
   * 표가 한 페이지에 안 들어갈 때, 같은 코트를 반복해 그리면서 행만 나눠 싣기 위한 것.
   */
  tableRowRange?: { start: number; end: number }
  /** 액션 표의 전체 행 수를 바깥에 알린다 — 몇 페이지로 나눌지 계산하는 데 쓴다 */
  onRowCount?: (count: number) => void
  /** 행의 세션 묶음 경계([start, end) 목록). All Session Avg. 행은 첫 묶음에 붙는다 — 빌더가 세션마다 아이템 하나로 나누는 데 쓴다 */
  onRowGroups?: (groups: Array<{ start: number; end: number }>) => void
  /**
   * 액션 상세(표·궤적)를 조회할 세션·선수 범위 (PDF 빌더용). 넘기면 이 범위로 한 번만 조회하고 idfSessions·idfPlayers
   * 는 클라이언트에서 거른다 — 응답이 세션·액션·선수 단위라 서버 필터와 결과가 같고, 아이템마다 다른 선수를 골라도
   * 요청은 하나다. 히트맵·존·포메이션은 합산 응답이라 그대로 idfSessions·idfPlayers 로 서버에 묻는다.
   */
  actionQuerySessionIds?: number[]
  actionQueryPlayerIds?: number[]
  /** 액션 표에 보일 컬럼 키 (PDF 빌더의 컬럼 체크박스). 없으면 전부. No. 열은 항상 보인다 */
  visibleColumns?: string[]
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  // 종목별 코트 배경 SVG (그림 자산이라 프론트 sportConfig에서 결정 → 맵에 prop으로 주입)
  const sportType = createAccountStore((s) => s.teamProfileInfo?.sportType)
  const courtSvg = getCourtSvg(sportType)
  const courtField = getCourtField(sportType) // 코트 내 실제 경기장 영역(럭비 등 여백 대응)

  const [fieldBounds, setFieldBounds] = useState<TrajectoryBounds>({
    minX: 0,
    maxX: 1000,
    minY: 0,
    maxY: 7000,
  })
  const [templatePathColor] = useState<string>(reportSection.settings.color)
  const pathColor = color ?? templatePathColor
  const [trajectoryData, setTrajectoryData] = useState<TrajectoryData[]>([])
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const [selectedMapType, setSelectedMapType] = useState<string>(
    reportSection.settings.defaultMapType,
  )
  const [selectedActionType, setSelectedActionType] = useState<string>(
    reportSection.settings.defaultActionType,
  )

  // 바깥(PDF 빌더 속성 패널)이 맵·액션 종류를 정하면 그대로 따른다
  useEffect(() => {
    if (mapType) {
      setSelectedMapType(mapType)
    }
  }, [mapType])
  useEffect(() => {
    if (actionType) {
      setSelectedActionType(actionType)
    }
  }, [actionType])

  const [sliderConfig, setSliderConfig] = useState({
    initialStartTime: 0,
    initialEndTime: 60,
    maxTime: 120,
    boundaryMin: 0,
    boundaryMax: 120,
  })

  const [rows, setRows] = useState<Record<string, number | string>[]>([])

  // 전체 행 수를 바깥에 알린다 (출력 시 페이지 분할 계산용)
  useEffect(() => {
    onRowCount?.(rows.length)
  }, [rows.length, onRowCount])

  // 세션 묶음 경계 — sessionGroup 이 바뀌는 자리마다 끊고, All Session Avg.(sessionGroup 0)는 첫 묶음에 붙인다
  const rowGroupKey = useMemo(() => {
    const starts: number[] = []
    rows.forEach((row, index) => {
      const prev = rows[index - 1]
      if (index === 0 || (row.sessionGroup !== prev.sessionGroup && prev.sessionGroup !== 0)) {
        starts.push(index)
      }
    })

    return starts.join(",")
  }, [rows])
  useEffect(() => {
    if (!onRowGroups) {
      return
    }
    const starts = rowGroupKey ? rowGroupKey.split(",").map(Number) : []
    onRowGroups(starts.map((start, index) => ({ start, end: starts[index + 1] ?? rows.length })))
  }, [rowGroupKey, rows.length, onRowGroups])

  // 이 인스턴스가 그릴 행 구간. 지정이 없으면 전부 그린다(화면 동작 불변).
  const visibleRows = useMemo(
    () => (tableRowRange ? rows.slice(tableRowRange.start, tableRowRange.end) : rows),
    [rows, tableRowRange],
  )
  const [filteredActions, setFilteredActions] = useState<ActionDetails[]>([])
  const [selectedSessionList, setSelectedSessionList] = useState<number[]>(idfSessions)
  const [selectedPlayerList, setSelectedPlayerList] = useState<number[]>(idfPlayers)
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(null)

  // 액션 상세를 넓은 범위(actionQuery*)로 받았을 때 이 인스턴스의 세션·선수로 거른다. 범위 프롭이 없으면 응답이 곧 선택이다.
  // 세션은 응답에 이름만 있어 액티비티 세션 이름으로 id 를 찾는다 — 못 찾은 세션은 표와 같은 기준으로 남긴다
  const hasActionScope = !!actionQuerySessionIds || !!actionQueryPlayerIds
  const scopePlayerActions = (actions: ActionDetails[]): ActionDetails[] =>
    hasActionScope
      ? actions.filter((action) => selectedPlayerList.includes(Number(action.idfPlayer)))
      : actions
  const scopeSessionActions = (
    sessions: Array<{ sessionName: string; actions: ActionDetails[] }>,
  ): ActionDetails[] => {
    const inScope = hasActionScope
      ? sessions.filter((session) => {
          const idfSession = activity.sessions.find(
            (s) => s.name === session.sessionName,
          )?.idfSession

          return idfSession === undefined || selectedSessionList.includes(idfSession)
        })
      : sessions

    return scopePlayerActions(inScope.flatMap((session) => session.actions))
  }

  const [action18ZoneList, setAction18ZoneList] = useState<ZoneData[]>([])
  const [location18ZoneList, setLocation18ZoneList] = useState<ZoneData[]>([])
  const [locationHeatmapList, setLocationHeatmapList] = useState<PointData[]>([])
  const [formationPointList, setFormationPointList] = useState<FormationPoint[]>([])
  const [heatmapMaxValue, setHeatmapMaxValue] = useState<number>(0)
  const [sessionRotationInfo, setSessionRotationInfo] = useState<SessionRotation[]>([])

  const contentRef = useRef<HTMLDivElement>(null)
  const prevSessionListRef = useRef<number[]>([])

  const searchParams = useSearchParams()
  const dailyReportType =
    activitySessionType ?? searchParams.get("dailyReportType") ?? ACTIVITY_SESSION_TYPE.SESSION
  const isActivityReport = dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY

  const tableData = new TacticTableData({
    settings: reportSection.settings,
    sort: new Sort("number", SORT_ORDER.ASC),
    tableHeader: [],
    tableBody: [],
    sessionList: [],
    sessionSelected: [],
    sessionSelectedRoot: [],
    activitySessionType: "activity",
  })

  const { data: metricList } = useMetricList()
  const { data: selectedUnitList } = useUnitQuery()
  const metricMap: MetricMapType =
    metricList?.reduce((map, obj) => {
      map.set(obj.key, obj)

      return map
    }, new Map()) || new Map()

  DEFAULT_COLUMNS.forEach((column, index) => {
    const isFixed = reportSection.settings.fixedColumns.includes(column.key)
    let unit = parseUnit(column.unit, selectedUnitList, metricMap) ?? null
    const decimalPoint = getDecimalPoint(column?.unit, unit)

    if (column.key === "duration") {
      unit = "sec"
    } else if (column.key === "interval") {
      unit = "min sec"
    }
    if (column.key === "occurrence") {
      unit = "min sec"
    }

    tableData.tableHeader.push(
      new TableHeaderColumn({
        key: column.key,
        title: column.key,
        unit: unit,
        decimalPoint: decimalPoint,
        indexColumn: index,
        isFixed,
      }),
    )
  })

  const exportActionCSV = () => {
    if (!rows || rows.length === 0) {
      return
    }

    const headers = tableData.tableHeader.map((header) => ({
      key: header.key,
      title: translateKey(header.key) || t(header.title),
      unit: header.unit,
    }))

    const csvRows = rows.map((row) => {
      const csvRow: Record<string, string | number | null> = {}

      headers.forEach((header) => {
        if (row.isAverageRow && header.key === "uniformNumber") {
          csvRow[header.key] = (row.sessionAverageLabel as string) || ""
        } else if (row.isAverageRow && (header.key === "number" || header.key === "occurrence")) {
          csvRow[header.key] = ""
        } else {
          csvRow[header.key] = (row[header.key] as string | number) ?? null
        }
      })

      return csvRow
    })

    const activityName = activity.name || ""
    const fileName = `${getDateTimeString()}_Activity_${activityName}_TacticMap_${selectedActionType}`

    exportTableFromView({ fileName, headers, rows: csvRows })
  }

  const getMenuItems = () => {
    const exportOptions = DEFAULT_EXPORT_OPTIONS.map((option) => ({
      ...option,
      func: async () => {
        await handleExport(option.value as "png" | "pdf")
      },
    }))

    const csvOption = {
      value: "csv",
      name: "CSV",
      group: "button.export",
      func: exportActionCSV,
    }

    return [...exportOptions, csvOption]
  }

  const handleExport = async (exportType: "png" | "pdf") => {
    if (!contentRef.current) {
      return
    }

    const valueLabels = document.querySelectorAll(".MuiSlider-valueLabel")
    valueLabels.forEach((label) => {
      ;(label as HTMLElement).style.display = "none"
    })

    const sliderInputs = contentRef.current.querySelectorAll('.MuiSlider-thumb input[type="range"]')
    sliderInputs.forEach((input) => {
      ;(input as HTMLElement).style.display = "none"
    })

    const tableWrapper = contentRef.current.querySelector(
      ".tactic-map-middle-table",
    ) as HTMLElement | null
    const tableComponent = contentRef.current.querySelector(
      ".tactic-map-middle-table-component",
    ) as HTMLElement | null
    const virtualScroller = contentRef.current.querySelector(
      ".MuiDataGrid-virtualScroller",
    ) as HTMLElement | null

    const originalStyles = {
      tableWrapper: {
        overflow: tableWrapper?.style.overflow || "",
        maxHeight: tableWrapper?.style.maxHeight || "",
      },
      tableComponent: {
        maxHeight: tableComponent?.style.maxHeight || "",
        height: tableComponent?.style.height || "",
      },
      virtualScroller: {
        overflow: virtualScroller?.style.overflow || "",
        maxHeight: virtualScroller?.style.maxHeight || "",
        height: virtualScroller?.style.height || "",
      },
    }

    if (tableWrapper) {
      tableWrapper.style.overflow = "visible"
      tableWrapper.style.maxHeight = "none"
    }
    if (tableComponent) {
      tableComponent.style.maxHeight = "none"
      tableComponent.style.height = "auto"
    }
    if (virtualScroller) {
      virtualScroller.style.overflow = "visible"
      virtualScroller.style.maxHeight = "none"
      virtualScroller.style.height = "auto"
    }

    await new Promise((resolve) => setTimeout(resolve, 100))

    try {
      await document.fonts.ready

      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: colors.neutral.white,
        scale: 3,
        onclone: inlineStylesheetsIntoClone,
        logging: false,
        useCORS: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        ignoreElements: (element) => {
          return (
            element.classList.contains("MuiSlider-valueLabel") ||
            element.classList.contains("MuiSlider-valueLabelLabel")
          )
        },
      })

      const fileName = `${getDateTimeString()}_activity_report_tactic_map`

      if (exportType === "png") {
        const link = document.createElement("a")
        link.download = `${fileName}.png`
        link.href = canvas.toDataURL("image/png")
        link.click()
      } else {
        const pdf = await PDF.create()
        pdf.appendCanvas(canvas)
        pdf.save(`${fileName}.pdf`)
      }
    } catch (error) {
      console.error("Export failed:", error)
    } finally {
      valueLabels.forEach((label) => {
        ;(label as HTMLElement).style.display = ""
      })

      sliderInputs.forEach((input) => {
        ;(input as HTMLElement).style.display = ""
      })

      if (tableWrapper) {
        tableWrapper.style.overflow = originalStyles.tableWrapper.overflow
        tableWrapper.style.maxHeight = originalStyles.tableWrapper.maxHeight
      }
      if (tableComponent) {
        tableComponent.style.maxHeight = originalStyles.tableComponent.maxHeight
        tableComponent.style.height = originalStyles.tableComponent.height
      }
      if (virtualScroller) {
        virtualScroller.style.overflow = originalStyles.virtualScroller.overflow
        virtualScroller.style.maxHeight = originalStyles.virtualScroller.maxHeight
        virtualScroller.style.height = originalStyles.virtualScroller.height
      }
    }
  }

  const handleTimeChange = (timeData: TimeChangeData): void => {
    const startTime = timeData.start * 60
    const endTime = timeData.end * 60

    setSliderConfig((prev) => ({
      ...prev,
      initialStartTime: timeData.start,
      initialEndTime: timeData.end,
    }))

    setTrajectoryData([])
    setRows([])
    setAction18ZoneList([])
    setLocation18ZoneList([])
    setLocationHeatmapList([])
    setFormationPointList([])

    if (isActivityReport && actionActivityDetails) {
      const actions = scopePlayerActions(actionActivityDetails.actions).filter((action) => {
        return action.occurrence >= startTime && action.occurrence <= endTime
      })
      setFilteredActions(actions)
    } else if (!isActivityReport && actionSessionDetails) {
      const actions = scopeSessionActions(actionSessionDetails.sessions).filter((action) => {
        return action.occurrence >= startTime && action.occurrence <= endTime
      })
      setFilteredActions(actions)
    }

    if (selectedMapType === "action18Zone") {
      if (isActivityReport && action18ZoneActivityData) {
        const zoneList = makeZoneList(action18ZoneActivityData, startTime, endTime, [])
        setAction18ZoneList(zoneList)
      } else if (!isActivityReport && action18ZoneSessionData) {
        const zoneList = makeZoneList(
          action18ZoneSessionData,
          startTime,
          endTime,
          sessionRotationInfo,
        )
        setAction18ZoneList(zoneList)
      }
    }

    if (selectedMapType === "formationMap") {
      if (isActivityReport && formationActivityData) {
        const formationMap = makeFormationMap(
          formationActivityData.formationList,
          startTime,
          endTime,
          [],
          formationActivityData.stadiumX,
          formationActivityData.stadiumY,
        )
        setFormationPointList(formationMap)
      } else if (!isActivityReport && formationSessionData) {
        const formationMap = makeFormationMap(
          formationSessionData.formationList,
          startTime,
          endTime,
          sessionRotationInfo,
          formationSessionData.stadiumX,
          formationSessionData.stadiumY,
        )
        setFormationPointList(formationMap)
      }
    }

    if (selectedMapType === "location18Zone") {
      if (isActivityReport && location18ZoneActivityData) {
        const zoneList = makeZoneList(location18ZoneActivityData, startTime, endTime, [])
        setLocation18ZoneList(zoneList)
      } else if (!isActivityReport && location18ZoneSessionData) {
        const zoneList = makeZoneList(
          location18ZoneSessionData,
          startTime,
          endTime,
          sessionRotationInfo,
        )
        setLocation18ZoneList(zoneList)
      }
    }

    if (selectedMapType === "locationHeatMap") {
      if (isActivityReport && locationHeatmapActivityData) {
        const heatmap = makeHeatmap(locationHeatmapActivityData, startTime, endTime, [])
        setLocationHeatmapList(heatmap)
      } else if (!isActivityReport && locationHeatmapSessionData) {
        const heatmap = makeHeatmap(
          locationHeatmapSessionData,
          startTime,
          endTime,
          sessionRotationInfo,
        )
        setLocationHeatmapList(heatmap)
      }
    }
  }

  useEffect(() => {
    if (idfSessions.length && idfPlayers.length) {
      setSelectedSessionList(idfSessions)
      setSelectedPlayerList(idfPlayers)
    }
  }, [idfSessions, idfPlayers])

  const { data: actionActivityDetails } = useReportPlayerActivityActionDetailsQuery({
    activities: activity.idfActivity,
    players: actionQueryPlayerIds ?? selectedPlayerList,
    actionType: selectedActionType,
    isActivity: isActivityReport,
  })

  const { data: action18ZoneActivityData } = useReportPlayerActivityAction18ZoneQuery({
    activities: activity.idfActivity,
    players: selectedPlayerList,
    actionType: selectedActionType,
    mapType: selectedMapType,
    isActivity: isActivityReport,
  })

  const { data: formationActivityData } = useReportPlayerActivityFormationMapQuery({
    activities: activity.idfActivity,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isActivity: isActivityReport,
  })

  const { data: location18ZoneActivityData } = useReportPlayerActivityLocation18ZoneQuery({
    activities: activity.idfActivity,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isActivity: isActivityReport,
  })

  const { data: locationHeatmapActivityData } = useReportPlayerActivityLocationHeatmapQuery({
    activities: activity.idfActivity,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isActivity: isActivityReport,
  })

  const { data: actionSessionDetails } = useReportPlayerSessionActionDetailsQuery({
    sessions: actionQuerySessionIds ?? selectedSessionList,
    players: actionQueryPlayerIds ?? selectedPlayerList,
    actionType: selectedActionType,
    isSession: !isActivityReport,
  })

  const { data: action18ZoneSessionData } = useReportPlayerSessionAction18ZoneQuery({
    sessions: selectedSessionList,
    players: selectedPlayerList,
    actionType: selectedActionType,
    mapType: selectedMapType,
    isSession: !isActivityReport,
  })

  const { data: formationSessionData } = useReportPlayerSessionFormationMapQuery({
    sessions: selectedSessionList,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isSession: !isActivityReport,
  })

  const { data: location18ZoneSessionData } = useReportPlayerSessionLocation18ZoneQuery({
    sessions: selectedSessionList,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isSession: !isActivityReport,
  })

  const { data: locationHeatmapSessionData } = useReportPlayerSessionLocationHeatmapQuery({
    sessions: selectedSessionList,
    players: selectedPlayerList,
    mapType: selectedMapType,
    isSession: !isActivityReport,
  })

  function filterHeatmapList(zoneData: PlayerHeatmapSummary, startTime: number, endTime: number) {
    return zoneData.heatmapList.filter((heatMapList) => {
      return heatMapList.minute >= startTime / 60 && heatMapList.minute <= endTime / 60
    })
  }

  function makeZoneList(
    zoneData: PlayerHeatmapSummary,
    startTime: number,
    endTime: number,
    sessionRotations: SessionRotation[],
  ) {
    const heatmapList = filterHeatmapList(zoneData, startTime, endTime)

    const counts = new Array(18).fill(0)

    heatmapList.forEach((heatmap) => {
      const currentMinuteInSeconds = heatmap.minute
      const needsRotation = sessionRotations.some(
        (rotation) =>
          rotation.isRoration &&
          currentMinuteInSeconds >= rotation.startMinutes &&
          currentMinuteInSeconds <= rotation.endMinutes,
      )

      let zoneIdx = 0
      heatmap.heatmap.forEach((row: number[]) => {
        row.forEach((value: number) => {
          if (needsRotation) {
            const rotatedIdx = 17 - zoneIdx
            counts[rotatedIdx] += value
          } else {
            counts[zoneIdx] += value
          }
          zoneIdx++
        })
      })
    })

    return counts.map((count, i) => ({
      zone: i + 1,
      count,
    }))
  }

  function makeHeatmap(
    zoneData: PlayerHeatmapSummary,
    startTime: number,
    endTime: number,
    sessionRotations: SessionRotation[],
  ) {
    // 백엔드 LocationService.GRID_COUNT_LOCATION_X/Y 와 동일해야 함 (행 96 × 열 72).
    // 값이 어긋나면 회전 보정 시 좌표가 그리드 밖으로 밀려 경기장 밖으로 새어 나간다.
    const GRID_COUNT_X = 96
    const GRID_COUNT_Y = 72

    const rotateCoordinates = (x: number, y: number, needsRotation: boolean) => {
      if (!needsRotation) {
        return { x, y }
      }

      return {
        x: GRID_COUNT_X - 1 - x,
        y: GRID_COUNT_Y - 1 - y,
      }
    }

    const globalGridMap = new Map<string, number>()

    zoneData.heatmapList.forEach((heatmapItem) => {
      heatmapItem.heatmap.forEach((row, x) => {
        row.forEach((value, y) => {
          const key = `${x},${y}`
          globalGridMap.set(key, (globalGridMap.get(key) || 0) + value)
        })
      })
    })

    const globalMaxValue = Math.max(...globalGridMap.values(), 0)
    setHeatmapMaxValue(globalMaxValue)

    const heatmapList = filterHeatmapList(zoneData, startTime, endTime)

    const gridMap = new Map<string, number>()
    heatmapList.forEach((heatmapItem) => {
      const currentMinuteInSeconds = heatmapItem.minute
      const needsRotation = sessionRotations.some(
        (rotation) =>
          rotation.isRoration &&
          currentMinuteInSeconds >= rotation.startMinutes &&
          currentMinuteInSeconds <= rotation.endMinutes,
      )

      heatmapItem.heatmap.forEach((row, x) => {
        row.forEach((value, y) => {
          const coords = rotateCoordinates(x, y, needsRotation)
          const key = `${coords.x},${coords.y}`
          gridMap.set(key, (gridMap.get(key) || 0) + value)
        })
      })
    })

    const pointDataList: PointData[] = []
    gridMap.forEach((value, key) => {
      if (value > 0) {
        const [x, y] = key.split(",").map(Number)
        pointDataList.push({ x, y, value })
      }
    })

    return pointDataList
  }

  function makeFormationMap(
    formationList: Formation[],
    startTime: number,
    endTime: number,
    sessionRotations: SessionRotation[],
    stadiumX: number,
    stadiumY: number,
  ) {
    const centerX = stadiumX / 2
    const centerY = stadiumY / 2

    const filteredData = formationList.filter(
      (item) => item.minute >= startTime / 60 && item.minute <= endTime / 60,
    )

    const allPoints = filteredData.flatMap((item) => {
      const currentMinuteInSeconds = item.minute
      const needsRotation = sessionRotations.some(
        (rotation) =>
          rotation.isRoration &&
          currentMinuteInSeconds >= rotation.startMinutes &&
          currentMinuteInSeconds <= rotation.endMinutes,
      )

      return item.pointList.map((point) => {
        if (needsRotation) {
          return {
            idfPlayer: point.idfPlayer,
            x: centerX - (point.x - centerX),
            y: centerY - (point.y - centerY),
          }
        }

        return point
      })
    })

    const groupedByPlayer = allPoints.reduce(
      (acc, point) => {
        if (!acc[point.idfPlayer]) {
          acc[point.idfPlayer] = []
        }
        acc[point.idfPlayer].push(point)

        return acc
      },
      {} as Record<number, Array<{ idfPlayer: number; x: number; y: number }>>,
    )

    const formationPoints: FormationPoint[] = Object.entries(groupedByPlayer).map(
      ([playerId, points]) => {
        const avgX = points.reduce((sum, p) => sum + p.x, 0) / points.length
        const avgY = points.reduce((sum, p) => sum + p.y, 0) / points.length

        return {
          idfPlayer: Number(playerId),
          x: avgX,
          y: avgY,
        }
      },
    )

    return formationPoints
  }

  useEffect(() => {
    if (isActivityReport && actionActivityDetails) {
      setFieldBounds({
        ...fieldBounds,
        maxX: actionActivityDetails.stadiumY,
        maxY: actionActivityDetails.stadiumX,
      })

      const actions = scopePlayerActions(actionActivityDetails.actions).filter((action) => {
        return (
          action.occurrence >= sliderConfig.initialStartTime * 60 &&
          action.occurrence <= sliderConfig.initialEndTime * 60
        )
      })

      setFilteredActions(actions)
    } else if (!isActivityReport && actionSessionDetails) {
      setFieldBounds({
        ...fieldBounds,
        maxX: actionSessionDetails.stadiumY,
        maxY: actionSessionDetails.stadiumX,
      })

      const actions = scopeSessionActions(actionSessionDetails.sessions).filter((action) => {
        return (
          action.occurrence >= sliderConfig.initialStartTime * 60 &&
          action.occurrence <= sliderConfig.initialEndTime * 60
        )
      })

      setFilteredActions(actions)
    }
    // scope 프롭이 있을 때만 selected*List 가 응답을 거르므로 그때만 다시 계산되게 키로 넣는다
  }, [
    actionActivityDetails,
    actionSessionDetails,
    isActivityReport,
    sliderConfig.initialStartTime,
    sliderConfig.initialEndTime,
    hasActionScope ? selectedSessionList.join(",") : "",
    hasActionScope ? selectedPlayerList.join(",") : "",
  ])

  useEffect(() => {
    if (isActivityReport && action18ZoneActivityData) {
      const zoneList = makeZoneList(
        action18ZoneActivityData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        [],
      )

      setAction18ZoneList(zoneList)
    } else if (!isActivityReport && action18ZoneSessionData) {
      const zoneList = makeZoneList(
        action18ZoneSessionData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        sessionRotationInfo,
      )

      setAction18ZoneList(zoneList)
    }
  }, [
    action18ZoneActivityData,
    action18ZoneSessionData,
    isActivityReport,
    sliderConfig.initialStartTime,
    sliderConfig.initialEndTime,
    sessionRotationInfo,
  ])

  useEffect(() => {
    if (isActivityReport && location18ZoneActivityData) {
      const zoneList = makeZoneList(
        location18ZoneActivityData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        [],
      )

      setLocation18ZoneList(zoneList)
    } else if (!isActivityReport && location18ZoneSessionData) {
      const zoneList = makeZoneList(
        location18ZoneSessionData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        sessionRotationInfo,
      )

      setLocation18ZoneList(zoneList)
    }
  }, [
    location18ZoneActivityData,
    location18ZoneSessionData,
    isActivityReport,
    sliderConfig.initialStartTime,
    sliderConfig.initialEndTime,
    sessionRotationInfo,
  ])

  useEffect(() => {
    if (isActivityReport && locationHeatmapActivityData) {
      const heatmap = makeHeatmap(
        locationHeatmapActivityData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        [],
      )

      setLocationHeatmapList(heatmap)
    } else if (!isActivityReport && locationHeatmapSessionData) {
      const heatmap = makeHeatmap(
        locationHeatmapSessionData,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        sessionRotationInfo,
      )

      setLocationHeatmapList(heatmap)
    }
  }, [
    locationHeatmapActivityData,
    locationHeatmapSessionData,
    isActivityReport,
    sliderConfig.initialStartTime,
    sliderConfig.initialEndTime,
    sessionRotationInfo,
  ])

  useEffect(() => {
    if (isActivityReport && formationActivityData) {
      const formationMap = makeFormationMap(
        formationActivityData.formationList,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        [],
        formationActivityData.stadiumX,
        formationActivityData.stadiumY,
      )

      setFormationPointList(formationMap)
    } else if (!isActivityReport && formationSessionData) {
      const formationMap = makeFormationMap(
        formationSessionData.formationList,
        sliderConfig.initialStartTime * 60,
        sliderConfig.initialEndTime * 60,
        sessionRotationInfo,
        formationSessionData.stadiumX,
        formationSessionData.stadiumY,
      )

      setFormationPointList(formationMap)
    }
  }, [
    formationActivityData,
    formationSessionData,
    isActivityReport,
    sliderConfig.initialStartTime,
    sliderConfig.initialEndTime,
    sessionRotationInfo,
  ])

  useEffect(() => {
    if (!activity) {
      return
    }

    const activityStartTime = new Date(activity.dateStart)
    const activityFinishTime = new Date(activity.dateFinish)
    const activityDurationInMillis = activityFinishTime.getTime() - activityStartTime.getTime()
    const activityDurationInMinutes = Math.floor(activityDurationInMillis / (1000 * 60))

    if (!isActivityReport && !!activity.sessions.length && !!selectedSessionList.length) {
      const prevSessionList = prevSessionListRef.current
      const sessionListChanged =
        prevSessionList.length !== selectedSessionList.length ||
        !prevSessionList.every((id, index) => id === selectedSessionList[index])

      const filteredSessions = activity.sessions.filter((s) =>
        selectedSessionList.includes(s.idfSession),
      )
      const sessionStartMills = Math.min(
        ...filteredSessions.map((s) => new Date(s.dateStart).getTime()),
      )
      const sessionFinishMills = Math.max(
        ...filteredSessions.map((s) => new Date(s.dateFinish).getTime()),
      )

      const relativeStartMinutes = Math.floor(
        (sessionStartMills - activityStartTime.getTime()) / (1000 * 60),
      )
      const relativeEndMinutes = Math.floor(
        (sessionFinishMills - activityStartTime.getTime()) / (1000 * 60),
      )

      if (sessionListChanged) {
        setSliderConfig((prev) => ({
          ...prev,
          initialStartTime: relativeStartMinutes,
          initialEndTime: relativeEndMinutes,
          maxTime: activityDurationInMinutes,
          boundaryMin: Math.max(0, relativeStartMinutes),
          boundaryMax: Math.min(activityDurationInMinutes, relativeEndMinutes),
        }))
        prevSessionListRef.current = [...selectedSessionList]
      } else {
        setSliderConfig((prev) => ({
          ...prev,
          maxTime: activityDurationInMinutes,
          boundaryMin: Math.max(0, relativeStartMinutes),
          boundaryMax: Math.min(activityDurationInMinutes, relativeEndMinutes),
        }))
      }

      const newRotationInfo = activity.sessions.map((session) => {
        const activityStartTime = new Date(activity.dateStart)
        const sessionStartMills = new Date(session.dateStart).getTime()
        const sessionFinishMills = new Date(session.dateFinish).getTime()

        const relativeStartMinutes = Math.floor(
          (sessionStartMills - activityStartTime.getTime()) / (1000 * 60),
        )
        const relativeEndMinutes = Math.floor(
          (sessionFinishMills - activityStartTime.getTime()) / (1000 * 60),
        )

        return {
          idfSession: session.idfSession,
          name: session.name,
          isRoration: session.rotated,
          startMinutes: relativeStartMinutes,
          endMinutes: relativeEndMinutes,
        }
      })

      const rotationChanged =
        sessionRotationInfo.length !== newRotationInfo.length ||
        newRotationInfo.some((newItem) => {
          const existing = sessionRotationInfo.find((s) => s.idfSession === newItem.idfSession)
          return !existing || existing.isRoration !== newItem.isRoration
        })

      if (rotationChanged) {
        setSessionRotationInfo(newRotationInfo)
      }

      return
    }

    setSliderConfig({
      initialStartTime: 0,
      initialEndTime: activityDurationInMinutes,
      maxTime: activityDurationInMinutes,
      boundaryMin: 0,
      boundaryMax: activityDurationInMinutes,
    })
  }, [activity, idfSessions, selectedSessionList, isActivityReport])

  useEffect(() => {
    if (!filteredActions || filteredActions.length === 0) {
      setTrajectoryData([])
      setRows([])

      return
    }

    const validActions = filteredActions.filter(
      (action) => action && action.trajectory && action.trajectory.length > 0,
    )
    const actionsToDisplay =
      selectedActionIndex !== null
        ? validActions.filter((action) => action === filteredActions[selectedActionIndex])
        : validActions

    const stadiumX = isActivityReport
      ? actionActivityDetails?.stadiumX || fieldBounds.maxX
      : actionSessionDetails?.stadiumX || fieldBounds.maxX
    const stadiumY = isActivityReport
      ? actionActivityDetails?.stadiumY || fieldBounds.maxY
      : actionSessionDetails?.stadiumY || fieldBounds.maxY
    const centerX = stadiumX / 2
    const centerY = stadiumY / 2

    const trajectoryData = actionsToDisplay.map((action) => {
      // 화살표 번호는 테이블 No.(filteredActions 내 위치)에 맞춘다 — 테이블은 백엔드 세션 소속으로 정확히 인덱싱.
      // 과거 occurrence-세션 시간창 재분류는 세션 겹침 구간 액션을 중복 카운트해 번호가 밀렸음 (WEB-2217).
      const displayIndex = filteredActions.indexOf(action) + 1

      const needsRotation = sessionRotationInfo.some(
        (rotation) =>
          rotation.isRoration &&
          action.occurrence >= rotation.startMinutes * 60 &&
          action.occurrence <= rotation.endMinutes * 60,
      )

      const rotatedTrajectory = needsRotation
        ? action.trajectory.map((point) => ({
            x: centerX - (point.x - centerX),
            y: centerY - (point.y - centerY),
          }))
        : action.trajectory

      return {
        value: displayIndex,
        trajectory: rotatedTrajectory,
      }
    })

    setTrajectoryData(trajectoryData)

    tableData.tableBody = []

    const sessionGroups: { sessionId: number; name: string; actions: any[] }[] = []

    if (!isActivityReport && actionSessionDetails?.sessions) {
      actionSessionDetails.sessions.forEach((session, index) => {
        const sessionInfo = sessionRotationInfo.find((info) => info.name === session.sessionName)

        if (sessionInfo && !selectedSessionList.includes(sessionInfo.idfSession)) {
          return
        }

        // 표는 응답 세션을 그대로 순회하므로(filteredActions 가 아님) 선수 범위를 여기서도 거른다
        const sessionActions = scopePlayerActions(session.actions).filter(
          (action) =>
            action.occurrence >= sliderConfig.initialStartTime * 60 &&
            action.occurrence <= sliderConfig.initialEndTime * 60,
        )

        if (sessionActions.length > 0) {
          sessionGroups.push({
            sessionId: sessionInfo?.idfSession || index,
            name: session.sessionName,
            actions: sessionActions,
          })
        }
      })
    } else {
      sessionGroups.push({
        sessionId: 0,
        name: t("allSessionAvg"),
        actions: filteredActions,
      })
    }

    const calculateAverageRow = (actions: any[], label: string) => {
      const row: Record<string, any> = {}

      tableData.tableHeader.forEach((header) => {
        if (
          header.key === "number" ||
          header.key === "uniformNumber" ||
          header.key === "occurrence"
        ) {
          row[header.key] = ""
        } else {
          const values = actions
            .map((action) => {
              if (header.key === "duration") {
                return action.metric[header.key]
              }

              return action.metric[header.key]
            })
            .filter((v) => !isNil(v))

          const avg =
            values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : null

          if (!isNil(avg)) {
            row[header.key] = getMetricValueStr({
              value: avg as number,
              unit:
                header.unit === "sec" || header.unit === "min sec" || header.unit === "occurrence"
                  ? "action_" + header.unit
                  : header.unit,
              decimalPoint: 1,
              calcType: "total",
              showUnit: false,
              metric: header.key,
            })
          } else {
            row[header.key] = null
          }
        }
      })

      row.sessionAverageLabel = label
      row.isAverageRow = true

      return row
    }

    const allRows: any[] = []

    if (filteredActions.length > 0) {
      const avgRow = calculateAverageRow(filteredActions, t("allSessionAvg"))
      avgRow.sortOrder = 0
      avgRow.sessionGroup = 0
      allRows.push(avgRow)
    }

    let globalIndex = 0
    sessionGroups.forEach((group, groupIndex) => {
      const sessionSortOrder = (groupIndex + 1) * 10000

      if (group.name !== t("allSessionAvg") && group.actions.length > 0) {
        const avgRow = calculateAverageRow(group.actions, `${group.name} ` + t("avg"))
        avgRow.sortOrder = sessionSortOrder
        avgRow.sessionGroup = groupIndex + 1
        allRows.push(avgRow)
      }

      group.actions.forEach((action) => {
        const row: Record<string, any> = {}
        const actualActionIndex = filteredActions.findIndex((a) => a === action)
        const displayIndex = globalIndex

        tableData.tableHeader.forEach((header) => {
          switch (header.key) {
            case "number":
              row[header.key] = displayIndex + 1
              break
            case "uniformNumber":
              row[header.key] = action[header.key]
              break
            default: {
              let value = action.metric[header.key]
              if (header.key === "occurrence") {
                value = action[header.key]
              }

              const result = !isNil(value)
                ? getMetricValueStr({
                    value: value as number,
                    unit:
                      header.unit === "sec" ||
                      header.unit === "min sec" ||
                      header.unit === "occurrence"
                        ? "action_" + header.unit
                        : header.unit,
                    decimalPoint: 1,
                    calcType: "total",
                    showUnit: false,
                    metric: header.key,
                  })
                : null
              row[header.key] = result
              break
            }
          }
        })
        row.isAverageRow = false
        row.sortOrder = sessionSortOrder + globalIndex + 1
        row.sessionGroup = groupIndex + 1
        row.actualActionIndex = actualActionIndex
        row.displayIndex = displayIndex
        allRows.push(row)
        globalIndex++
      })
    })

    setRows(allRows.map((row, idx) => ({ ...row, rowIndex: idx })))
  }, [
    filteredActions,
    selectedActionIndex,
    sessionRotationInfo,
    isActivityReport,
    hasActionScope ? selectedPlayerList.join(",") : "",
  ])

  const columns: GridColDef[] = useMemo(() => {
    const fixedColumnKeys = reportSection.settings.fixedColumns || []
    const visibleHeaders = visibleColumns
      ? tableData.tableHeader.filter(
          (header) => header.key === "number" || visibleColumns.includes(header.key),
        )
      : tableData.tableHeader

    return visibleHeaders.map((header) => {
      const headerTitle = translateKey(header.key) ?? t(header.title)
      const unitText = header.unit ? convertUnitSquare(header.unit) : ""

      const isFixedColumn = fixedColumnKeys.includes(header.key)

      // 출력용은 정렬 아이콘이 없고 패딩도 좁으므로 그만큼 최소 폭을 줄인다.
      const paddingWidth = compactTable ? 14 : 32
      const iconWidth = compactTable ? 0 : 20
      // fitColumns(PDF 빌더): 머리글은 단어마다 줄을 바꾸고 글자가 10px 이라, 가장 긴 단어 폭(14px 실측 × 10/14)이 최소 폭이다
      const FIT_FONT_RATIO = 10 / 14
      const headerWidth = fitColumns
        ? Math.ceil(
            Math.max(
              ...headerTitle.split(" ").map((word) => getTextWidth(word, paddingWidth, iconWidth)),
            ) * FIT_FONT_RATIO,
          )
        : getTextWidth(headerTitle, paddingWidth, iconWidth)
      const unitWidth = unitText
        ? Math.ceil(
            getTextWidth(unitText, paddingWidth, iconWidth) * (fitColumns ? FIT_FONT_RATIO : 1),
          )
        : 0
      const calculatedWidth = Math.max(headerWidth, unitWidth)
      const minWidth = fitColumns
        ? 28
        : compactTable
          ? (COMPACT_MIN_COLUMN_WIDTH[header.key] ?? COMPACT_MIN_COLUMN_WIDTH_DEFAULT)
          : 60
      const columnWidth = Math.max(calculatedWidth, minWidth)

      return {
        field: header.key,
        minWidth: columnWidth,
        // fitColumns: 고정 컬럼도 함께 비율 분배 — 표가 컨테이너 폭을 정확히 채운다 (PDF 빌더).
        // 순번(No.)은 값이 짧아 절반 비율만 차지하고 남는 폭을 지표 컬럼에 돌린다
        flex: isFixedColumn && !fitColumns ? 0 : fitColumns && header.key === "number" ? 0.5 : 1,
        ...(fitColumns ? { resizable: false } : {}),
        // 고정 컬럼(flex: 0)은 width 를 안 주면 DataGrid 기본값 100px 을 쓴다.
        // 출력용에서는 그 기본값 때문에 표가 넘치므로 계산한 폭을 명시한다.
        ...(compactTable && !fitColumns ? { width: columnWidth } : {}),
        sortComparator: (v1, v2, param1, param2) => {
          const row1 = param1.api.getRow(param1.id)
          const row2 = param2.api.getRow(param2.id)

          if (!row1 || !row2) {
            return 0
          }

          const sortModel = param1.api.state.sorting.sortModel
          const currentField = header.key
          const sortItem = sortModel.find((item) => item.field === currentField)
          const isDescending = sortItem?.sort === "desc"

          if (row1.sessionGroup !== row2.sessionGroup) {
            const groupOrder = row1.sessionGroup - row2.sessionGroup

            return isDescending ? -groupOrder : groupOrder
          }

          if (row1.isAverageRow && !row2.isAverageRow) {
            return isDescending ? 1 : -1
          }
          if (!row1.isAverageRow && row2.isAverageRow) {
            return isDescending ? -1 : 1
          }
          if (row1.isAverageRow && row2.isAverageRow) {
            return 0
          }

          if (v1 === v2) {
            return 0
          }
          if (v1 === null || v1 === undefined || v1 === "" || v1 === "-") {
            return 1
          }
          if (v2 === null || v2 === undefined || v2 === "" || v2 === "-") {
            return -1
          }

          const num1 = typeof v1 === "string" ? parseFloat(v1.replace(/[^\d.-]/g, "")) : v1
          const num2 = typeof v2 === "string" ? parseFloat(v2.replace(/[^\d.-]/g, "")) : v2

          if (!isNaN(num1) && !isNaN(num2)) {
            return num1 - num2
          }

          return String(v1).localeCompare(String(v2))
        },
        renderHeader: () => {
          const newHeaderTitle = translateKey(header.key)

          return (
            // fitColumns: 두 단어 이상은 단어마다 줄을 바꿔 컬럼을 좁게 쓴다 (PDF 빌더)
            <div style={fitColumns ? { whiteSpace: "pre-line" } : undefined}>
              {fitColumns ? (newHeaderTitle ?? "").split(" ").join("\n") : newHeaderTitle}
              <br />
              {header.unit && (
                <span className="report-stats-table-header-unit">
                  {convertUnitSquare(header.unit)}
                </span>
              )}
            </div>
          )
        },
        renderCell: (params) => {
          if (params.row.isAverageRow && header.key === "uniformNumber") {
            return (
              <div
                style={{
                  fontWeight: 600,
                  textAlign: "left",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={params.row.sessionAverageLabel || ""}>
                {params.row.sessionAverageLabel || ""}
              </div>
            )
          }

          if (params.row.isAverageRow && (header.key === "number" || header.key === "occurrence")) {
            return <div></div>
          }

          const isAllSessionAvg = params.row.isAverageRow && params.row.sessionGroup === 0

          return (
            <div style={isAllSessionAvg ? { fontWeight: 600 } : undefined}>
              {params.value ?? "-"}
            </div>
          )
        },
        // unit 플래그가 있으면 Table 이 머리글을 58px 로 고정한다 — fitColumns(빌더)는 촘촘한 48px 을 쓰도록 끈다
        unit: fitColumns ? false : !!header.unit,
      }
    })
  }, [tableData, t, translateKey, compactTable, fitColumns, visibleColumns])

  useEffect(() => {
    const gridElements = document?.querySelectorAll(
      ".tactic-map-middle-table-component .MuiDataGrid-virtualScroller",
    )
    if (!gridElements) {
      return
    }
    const listeners: Array<() => void> = []
    gridElements.forEach((gridElement) => {
      const onScroll = () => {
        const maxScrollLeft = gridElement.scrollWidth - gridElement.clientWidth - 31
        if (gridElement.scrollLeft >= maxScrollLeft) {
          gridElement.scrollLeft = maxScrollLeft
        }
      }

      gridElement.addEventListener("scroll", onScroll, { passive: false })
      listeners.push(() => gridElement.removeEventListener("scroll", onScroll))
    })

    return () => {
      listeners.forEach((removeListener) => removeListener())
    }
  }, [columns])

  const mapComponent = useMemo(() => {
    switch (selectedMapType) {
      case "actionTrajectory":
        return (
          <TrajectoryMap
            key={`trajectory-${trajectoryData.length}`}
            trajectoryData={trajectoryData}
            pathColor={pathColor}
            fieldBounds={fieldBounds}
            width={348}
            height={540}
            backgroundSvg={courtSvg}
            courtField={courtField}
          />
        )
      case "action18Zone":
        return (
          <ZoneMap
            data={action18ZoneList}
            color={pathColor}
            width={348}
            height={540}
            backgroundSvg={courtSvg}
            courtField={courtField}
          />
        )
      case "formationMap":
        return (
          <FormationMap
            formationPointList={formationPointList}
            playerList={
              isActivityReport
                ? formationActivityData?.playerList || []
                : formationSessionData?.playerList || []
            }
            width={348}
            height={540}
            stadiumX={
              isActivityReport
                ? formationActivityData?.stadiumX || 0
                : formationSessionData?.stadiumX || 0
            }
            stadiumY={
              isActivityReport
                ? formationActivityData?.stadiumY || 0
                : formationSessionData?.stadiumY || 0
            }
            backgroundSvg={courtSvg}
            courtField={courtField}
            showMeasurements={showFormationMeasurements(sportType)}
          />
        )
      case "locationHeatMap":
        return (
          <HeatMap
            data={locationHeatmapList}
            color={pathColor}
            width={348}
            height={540}
            maxValue={heatmapMaxValue}
            backgroundSvg={courtSvg}
            courtField={courtField}
          />
        )
      case "location18Zone":
        return (
          <ZoneMap
            data={location18ZoneList}
            color={pathColor}
            width={348}
            height={540}
            backgroundSvg={courtSvg}
            courtField={courtField}
          />
        )
      default:
        return <div />
    }
  }, [
    selectedMapType,
    trajectoryData,
    pathColor,
    fieldBounds,
    action18ZoneList,
    locationHeatmapList,
    location18ZoneList,
    formationPointList,
    formationActivityData,
    formationSessionData,
    isActivityReport,
    heatmapMaxValue,
    courtSvg,
    courtField,
  ])

  const showMap = variant !== "table"
  const showTable = variant !== "map"

  const content = (
    <div ref={contentRef}>
      {!hideControls && (
        <TimeRangeSliderContainer
          initialStartTime={sliderConfig.initialStartTime}
          initialEndTime={sliderConfig.initialEndTime}
          minDistance={1}
          maxTime={sliderConfig.maxTime}
          boundaryMin={sliderConfig.boundaryMin}
          boundaryMax={sliderConfig.boundaryMax}
          step={1}
          onTimeChange={handleTimeChange}
          onSliding={() => {}}
          containerStyle={{
            margin: "0px auto 36px auto",
          }}
        />
      )}
      <div className="tactic-map-middle-container">
        {showMap && (
          <div className="tactic-map-middle-components map-components">
            {(showRotationSelector ?? !hideControls) && (
              <div className="tactic-map-middle-components-rotation">
                {!isActivityReport && !!sessionRotationInfo.length && (
                  <MapRotationSelector
                    sessionRotationInfo={sessionRotationInfo}
                    setSessionRotationInfo={setSessionRotationInfo}
                  />
                )}
              </div>
            )}
            {mapComponent}
          </div>
        )}
        {showTable && (
          <div className="tactic-map-middle-table ReportStatsTableCont">
            <Table
              className="tactic-map-middle-table-component"
              columns={columns}
              rows={visibleRows}
              getRowId={(row) =>
                `${row.uniformNumber}-${row.number}-${row.occurrence}-${row.rowIndex}`
              }
              hasSummaryRow={true}
              disableVirtualization={true}
              disableColumnMenu={true}
              disableColumnResize={fitColumns}
              // fitColumns(PDF 빌더): 글자 10px 에 맞춘 촘촘한 행·머리글 — BuilderActionTable 의 분할 상수와 같은 값.
              // autoHeight: DataGrid 가 컨테이너 높이에 맞춰 행을 가상화·스크롤하지 않고 내용 높이 그대로 그린다 —
              // 빌더가 칸 높이를 계산하기 전 잠깐이라도 컨테이너가 좁으면 안 보이는 스크롤 뒤로 행이 잘렸다 (2026-09-29 요청)
              {...(fitColumns ? { rowHeight: 30, columnHeaderHeight: 48, autoHeight: true } : {})}
              slots={{
                pagination: overflowPagination(visibleRows.length),
              }}
              pinnedColumns={{
                // fitColumns: 모든 컬럼이 컨테이너 안에 들어가 가로 스크롤이 없으므로 고정이 필요 없고,
                // 고정 컬럼의 흰 배경 오버레이가 옆 컬럼 글자 끝("No." 의 점)을 덮는 문제도 피한다
                left: (() => {
                  if (fitColumns) {
                    return []
                  }
                  const fixedColumns = reportSection.settings.fixedColumns || []
                  const priorityOrder = ["number", "uniformNumber", "occurrence"]

                  const priorityColumns = priorityOrder.filter((col) => fixedColumns.includes(col))
                  const otherColumns = fixedColumns.filter((col) => !priorityOrder.includes(col))

                  return [...priorityColumns, ...otherColumns]
                })(),
              }}
              onRowClick={(params) => {
                if (params.row.isAverageRow) {
                  return
                }

                const clickedIndex = params.row.actualActionIndex
                if (clickedIndex === undefined || clickedIndex < 0) {
                  console.warn("Row clicked but actualActionIndex is invalid:", params.row)

                  return
                }

                if (selectedActionIndex === clickedIndex) {
                  setSelectedActionIndex(null)
                } else {
                  setSelectedActionIndex(clickedIndex)
                }
              }}
              getRowClassName={(params) => {
                if (params.row.isAverageRow) {
                  return "average-row"
                }

                const rowIndex = params.row.actualActionIndex

                return selectedActionIndex === rowIndex ? "selected-action-row" : ""
              }}
            />
          </div>
        )}
      </div>
    </div>
  )

  if (hideControls) {
    // PDF 빌더 캔버스 — 제목·메뉴 없이 내용만 (아이템 칩·제목은 CanvasItem 이 그린다)
    return <div className={`TacticMapSection TacticMapSection--${variant}`}>{content}</div>
  }

  return (
    <div className="TacticMapSection">
      <SectionContainer
        title={reportSection.title}
        sectionMenu={
          <div className="TacticMapSectionMenuContainer">
            <TacticMapFilter
              open={filterOpen}
              setOpen={setFilterOpen}
              selectedMapType={selectedMapType}
              setSelectedMapType={setSelectedMapType}
              sessionList={idfSessions}
              selectedSessionList={selectedSessionList}
              setSelectedSessionList={setSelectedSessionList}
              playerList={idfPlayers}
              selectedPlayerList={selectedPlayerList}
              setSelectedPlayerList={setSelectedPlayerList}
              selectedActionType={selectedActionType}
              setSelectedActionType={setSelectedActionType}
              activityDetail={activity}
            />
            <SectionMenu menuItems={getMenuItems()} />
          </div>
        }
        loading={loading}>
        {content}
      </SectionContainer>
    </div>
  )
}
